<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <div class="mb-4">
            <a class="text-primary text-body-2" style="cursor: pointer;" @click="backToList">‹ {{ $t('labelOrder.label.backToList') }}</a>
          </div>
          <v-alert v-if="flash" type="success" density="compact" closable class="mb-4" :text="flash" @click:close="flash = ''" />
          <v-alert v-if="errorMessage" type="error" density="compact" closable class="mb-4" :text="errorMessage" @click:close="errorMessage = ''" />
          <v-progress-linear v-if="isLoading" indeterminate color="indigo" />

          <template v-if="detail">
            <!-- 申請情報 -->
            <v-card variant="outlined" class="mb-4">
              <div class="d-flex flex-wrap">
                <div class="info-cell">
                  <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ $t('labelOrder.label.requestId') }}</div>
                  <div class="font-weight-bold" style="font-family: monospace;">{{ detail.requestId }}</div>
                </div>
                <div class="info-cell">
                  <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ $t('labelOrder.label.requestDate') }}</div>
                  <div>{{ detail.requestDate }}</div>
                </div>
                <div class="info-cell">
                  <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ $t('labelOrder.label.requestUser') }}</div>
                  <div>{{ detail.requestUserName || detail.requestUserId }}</div>
                </div>
                <div class="info-cell">
                  <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ $t('labelOrder.label.status') }}</div>
                  <v-chip size="small" label variant="tonal" :color="statusColor(detail.status)">{{ $t('labelOrder.status.' + detail.status) }}</v-chip>
                </div>
                <div class="info-cell">
                  <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ $t('labelOrder.label.deliveryDate') }}</div>
                  <div v-if="detail.deliveryDate" class="font-weight-bold">{{ detail.deliveryDate }}</div>
                  <div v-else class="text-disabled">{{ $t('labelOrder.label.notSet') }}</div>
                </div>
                <div class="info-cell">
                  <div class="text-caption text-medium-emphasis font-weight-bold mb-1">{{ $t('labelOrder.label.reviewer') }}</div>
                  <template v-if="detail.reviewUserName">
                    <div>{{ detail.reviewUserName }}</div>
                    <div class="text-caption text-medium-emphasis">{{ detail.reviewedDatetime }}</div>
                  </template>
                  <div v-else class="text-disabled">—</div>
                </div>
              </div>
              <template v-if="detail.reviewNote">
                <v-divider></v-divider>
                <div class="px-5 py-3 text-body-2 bg-grey-lighten-5">
                  <strong>{{ detail.status === STATUS.CANCELED ? $t('labelOrder.label.cancelReason') : $t('labelOrder.label.reviewNote') }}：</strong>{{ detail.reviewNote }}
                </div>
              </template>
            </v-card>

            <!-- サマリ -->
            <v-row dense class="mb-2">
              <v-col cols="3">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-caption text-medium-emphasis font-weight-bold">{{ $t('labelOrder.label.summary.requestedTotal') }}</div>
                  <div class="text-h6">{{ num(sums.requested) }} {{ $t('labelOrder.label.unitSheet') }}</div>
                </v-card>
              </v-col>
              <v-col cols="3">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-caption text-medium-emphasis font-weight-bold">{{ $t('labelOrder.label.summary.approvedTotal') }}</div>
                  <div class="text-h6 text-blue-darken-2">{{ num(sums.approved) }} {{ $t('labelOrder.label.unitSheet') }}</div>
                </v-card>
              </v-col>
              <v-col cols="3">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-caption text-medium-emphasis font-weight-bold">{{ $t('labelOrder.label.summary.diff') }}</div>
                  <div class="text-h6" :class="diffClass(sums.approved - sums.requested)">{{ signed(sums.approved - sums.requested) }} {{ $t('labelOrder.label.unitSheet') }}</div>
                </v-card>
              </v-col>
              <v-col cols="3">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-caption text-medium-emphasis font-weight-bold">{{ $t('labelOrder.label.summary.changedCount') }}</div>
                  <div class="text-h6 text-amber-darken-3">{{ sums.changed }} {{ $t('labelOrder.label.unitCount') }}</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- 明細 -->
            <v-table density="compact" class="text-no-wrap">
              <thead>
                <tr>
                  <th>{{ $t('labelOrder.label.lineTable.siteCode') }}</th>
                  <th>{{ $t('labelOrder.label.lineTable.storeName') }}</th>
                  <th>{{ $t('labelOrder.label.lineTable.janCode') }}</th>
                  <th class="text-right">{{ $t('labelOrder.label.lineTable.requestedQty') }}</th>
                  <th class="text-right" style="width: 160px;">{{ $t('labelOrder.label.lineTable.approvedQty') }}</th>
                  <th class="text-right">{{ $t('labelOrder.label.lineTable.diff') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="l in rows" :key="l.siteCode" :class="l.diff !== 0 ? 'bg-amber-lighten-5' : ''">
                  <td class="text-medium-emphasis" style="font-family: monospace;">{{ l.siteCode }}</td>
                  <td class="font-weight-medium">{{ l.storeName }}</td>
                  <td class="text-medium-emphasis" style="font-family: monospace;">{{ l.janCode }}</td>
                  <td class="text-right">{{ num(l.requestedQty) }}</td>
                  <td class="text-right">
                    <v-text-field
                      v-if="editing"
                      v-model="editQty[l.siteCode]"
                      type="number"
                      min="0"
                      step="500"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="text-right-input"
                    />
                    <span v-else-if="l.approvedQty != null" class="font-weight-bold">{{ num(l.approvedQty) }}</span>
                    <span v-else class="text-disabled">—</span>
                  </td>
                  <td class="text-right">
                    <span v-if="l.approvedQty == null" class="text-disabled">—</span>
                    <span v-else-if="l.diff === 0" class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.noChange') }}</span>
                    <v-chip v-else size="small" label variant="tonal" :color="l.diff > 0 ? 'blue-darken-1' : 'red-darken-1'">{{ signed(l.diff) }}</v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- 操作 -->
            <v-card variant="outlined" class="mt-4 pa-4">
              <div v-if="!admin" class="d-flex align-center text-body-2 text-medium-emphasis">
                <v-chip size="small" label class="mr-3">{{ $t('labelOrder.label.readOnly') }}</v-chip>
                {{ $t('labelOrder.label.readOnlyNote') }}
              </div>
              <div v-else-if="editing" class="d-flex align-center">
                <v-alert type="warning" variant="tonal" density="compact" class="flex-grow-1 mr-4" :text="$t('labelOrder.label.editMode')" />
                <v-btn variant="text" class="mr-2" @click="stopEdit">{{ $t('labelOrder.action.stopEdit') }}</v-btn>
                <v-btn class="px-6 bg-amber-darken-2" elevation="2" @click="approveDialog = true">{{ $t('labelOrder.action.doApproveEdited') }}</v-btn>
              </div>
              <div v-else-if="detail.status === STATUS.REQUESTED" class="d-flex align-center">
                <v-btn class="px-6 bg-green-darken-1 mr-3" elevation="2" @click="approveDialog = true">{{ $t('labelOrder.action.approve') }}</v-btn>
                <v-btn class="px-6 bg-amber-darken-2" elevation="2" @click="startEdit">{{ $t('labelOrder.action.approveEdited') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn variant="outlined" color="red-darken-1" @click="openCancel">{{ $t('labelOrder.action.cancel') }}</v-btn>
              </div>
              <div v-else class="text-body-2 text-medium-emphasis">
                {{ $t('labelOrder.label.cannotOperate', [$t('labelOrder.status.' + detail.status)]) }}
              </div>
            </v-card>
          </template>
        </v-container>

        <LabelApproveDialog
          v-model="approveDialog"
          :request-id="detail?.requestId"
          :edited="editing"
          :lines="editedLines"
          :changed-count="sums.changed"
          :approved-total="sums.approved"
          :line-count="rows.length"
          @approved="onApproved"
        />

        <!-- キャンセル -->
        <v-dialog v-model="cancelDialog" width="560" persistent>
          <v-card>
            <v-card-title>{{ $t('labelOrder.dialog.cancelTitle') }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-body-2 mb-4">{{ $t('labelOrder.dialog.cancelText') }}</div>
              <v-alert v-if="cancelError" type="error" density="compact" class="mb-4" :text="cancelError" />
              <v-textarea
                v-model="cancelReason"
                :label="$t('labelOrder.label.cancelReason') + ' *'"
                :placeholder="$t('labelOrder.label.cancelReasonPlaceholder')"
                variant="outlined"
                density="compact"
                rows="3"
                hide-details="auto"
                counter="500"
              />
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn variant="text" :disabled="isSubmitting" @click="cancelDialog = false">{{ $t('labelOrder.action.back') }}</v-btn>
              <v-btn class="px-6 bg-red-darken-1" elevation="2" :loading="isSubmitting" @click="doCancel">{{ $t('labelOrder.action.doCancel') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import LabelApproveDialog from "@/components/label/LabelApproveDialog";
import api from "@/apis/staff";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import { ORDER_STATUS, statusColor, num, signed, isAdmin } from "@/utils/labelManagement";

export default {
  components: { AppWrapperView, LabelApproveDialog },
  data: () => ({
    STATUS: ORDER_STATUS,
    admin: false,
    detail: null,
    isLoading: false,
    flash: "",
    errorMessage: "",
    // 修正して承認
    editing: false,
    editQty: {},
    approveDialog: false,
    // キャンセル
    cancelDialog: false,
    cancelReason: "",
    cancelError: "",
    isSubmitting: false,
  }),
  computed: {
    // 表示用の明細。修正中は入力中の値を確定枚数として差分を出す
    rows() {
      const lines = this.detail?.lines ?? [];
      return lines.map((l) => {
        const approvedQty = this.editing ? Number(this.editQty[l.siteCode] ?? l.requestedQty) : l.approvedQty;
        const diff = approvedQty == null ? 0 : approvedQty - l.requestedQty;
        return { ...l, approvedQty, diff };
      });
    },
    sums() {
      // 承認前は申請枚数を確定見込みとして数える。キャンセルは 0
      const canceled = this.detail?.status === ORDER_STATUS.CANCELED;
      return this.rows.reduce(
        (a, l) => ({
          requested: a.requested + l.requestedQty,
          approved: a.approved + (l.approvedQty ?? (canceled ? 0 : l.requestedQty)),
          changed: a.changed + (l.diff !== 0 ? 1 : 0),
        }),
        { requested: 0, approved: 0, changed: 0 }
      );
    },
    editedLines() {
      return this.rows.map((l) => ({ siteCode: l.siteCode, qty: l.approvedQty }));
    },
  },
  mounted() {
    this.admin = isAdmin();
    this.load();
  },
  methods: {
    num,
    signed,
    statusColor,
    diffClass(v) {
      if (v === 0) return "text-medium-emphasis";
      return v > 0 ? "text-blue-darken-2" : "text-red-darken-1";
    },
    load() {
      const requestId = this.$route.query.requestId;
      if (!requestId) {
        this.backToList();
        return;
      }
      this.isLoading = true;
      api.post("/label-order/detail", { requestId })
        .then((response) => {
          if (response.data?.status == 1) {
            this.errorMessage = response.data.errorMessage;
            this.detail = null;
            return;
          }
          this.detail = response.data.results;
        })
        .catch(() => {
          this.detail = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    startEdit() {
      this.editQty = {};
      (this.detail?.lines ?? []).forEach((l) => (this.editQty[l.siteCode] = l.requestedQty));
      this.editing = true;
    },
    stopEdit() {
      this.editing = false;
      this.editQty = {};
    },
    onApproved(deliveryDate) {
      this.flash = this.$t("labelOrder.message.approved", [this.detail.requestId, deliveryDate]);
      this.stopEdit();
      this.load();
    },
    openCancel() {
      this.cancelReason = "";
      this.cancelError = "";
      this.cancelDialog = true;
    },
    doCancel() {
      this.isSubmitting = true;
      this.cancelError = "";
      api.post("/label-order/cancel", { requestId: this.detail.requestId, reviewNote: this.cancelReason })
        .then((response) => {
          if (response.data?.status == 1) {
            this.cancelError = response.data.errorMessage;
            return;
          }
          this.cancelDialog = false;
          this.flash = this.$t("labelOrder.message.canceled");
          this.load();
        })
        .catch(() => {
          this.cancelError = this.$t("common.message.error");
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    backToList() {
      router.push({ name: Paths.LABEL_ORDER });
    },
  },
};
</script>

<style scoped>
.info-cell {
  flex: 1 1 160px;
  padding: 14px 18px;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
}
.text-right-input :deep(input) {
  text-align: right;
}
</style>
